import { useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import cn from "classnames";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { FiCopy, FiExternalLink, FiPower } from "react-icons/fi";

import { shortenAddress } from "@utils/shorten-address";
import {
  MAX_ACCEPTABLE_PRIORITY_FEE,
  PrioritizationFeeType,
  usePriorityFee,
} from "contexts/PriorityFeeContext";
import { useToast } from "contexts/ToastContext";
import { useWalletInfo } from "contexts/WalletInfoContext";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useMarinadeState } from "hooks/useMarinadeState";
import { IconButton } from "tw/components/atoms/IconButton";
import { format2Dec } from "utils/number-to-short-version";

export interface WalletBodyProps {
  closeSettingsPopup: () => void;
}

export const WalletBody: FC<WalletBodyProps> = ({ closeSettingsPopup }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    getPriorityFeeForType,
    setCustomPriorityFee: setPriorityFee,
    setFeeType,
    priorityFeeType,
  } = usePriorityFee();
  const { disconnect, wallet } = useWallet();
  const { walletAddress, SOLBalance } = useWalletInfo();
  const marinadeState = useMarinadeState();
  const [customFeeLS, setCustomFeeLS] = useLocalStorageState(
    "userTransactionPriorityFee",
    ""
  );

  const solBalance = (SOLBalance ?? 0) / LAMPORTS_PER_SOL;
  const solPrice = marinadeState?.solPrice.usd ?? 0;
  const walletAddressStr = walletAddress?.toString() ?? "";
  const subtitle = wallet?.adapter.name || "";
  const walletIcon = wallet?.adapter.icon || "";

  const [customPriorityFee, setCustomPriorityFee] = useState<string>(
    customFeeLS || getPriorityFeeForType(priorityFeeType)
  );

  const tabsContent = useMemo(
    () => [
      {
        title: t("app.priorityFee.low"),
        type: PrioritizationFeeType.LOW,
      },
      {
        title: t("app.priorityFee.medium"),
        type: PrioritizationFeeType.MEDIUM,
      },
      {
        title: t("app.priorityFee.fast"),
        type: PrioritizationFeeType.HIGH,
      },
      {
        title: t("app.priorityFee.custom"),
        type: PrioritizationFeeType.CUSTOM,
      },
    ],
    [t]
  );

  const handleTabChange = useCallback(
    (tab: PrioritizationFeeType) => {
      setFeeType(tab);
      setCustomPriorityFee(getPriorityFeeForType(tab));
      setCustomFeeLS(getPriorityFeeForType(tab));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getPriorityFeeForType]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value.replace(/[^0-9.]/g, "");

      if (value.startsWith(".")) {
        value = `0${value}`;
      }

      if (Number(value) > MAX_ACCEPTABLE_PRIORITY_FEE) {
        value = MAX_ACCEPTABLE_PRIORITY_FEE.toString();
      }

      setCustomPriorityFee(value);
      setFeeType(PrioritizationFeeType.CUSTOM);

      setCustomFeeLS(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDisconnectAndClose = useCallback(() => {
    closeSettingsPopup();
    disconnect();
  }, [closeSettingsPopup, disconnect]);

  const handleInputBlur = useCallback(() => {
    if (parseFloat(customPriorityFee) === 0) {
      setCustomPriorityFee("0");
      setCustomFeeLS("0");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPriorityFee]);

  useEffect(() => {
    setPriorityFee(customPriorityFee);
  }, [customPriorityFee, setPriorityFee]);

  useEffect(() => {
    if (priorityFeeType !== PrioritizationFeeType.CUSTOM) {
      setCustomPriorityFee(getPriorityFeeForType(priorityFeeType));
    }
  }, [priorityFeeType, getPriorityFeeForType]);

  return (
    <div className="pb-16-px mt-10-px md:px-0 pl-4-px pr-12-px">
      {wallet && (
        <>
          <div className="flex gap-2 text-left items-center">
            <Image src={walletIcon} alt="Wallet icon" height={40} width={40} />
            <div className="flex-grow flex flex-col justify-center ml-4-px">
              <span className="button-m block">
                {shortenAddress(walletAddressStr)}
              </span>
              <span className="text-sm text-tertiary-text">{subtitle}</span>
            </div>
            <IconButton
              icon={<FiCopy />}
              onClick={() => {
                navigator.clipboard.writeText(walletAddressStr);
                showToast({
                  status: "success",
                  title: "Successfully copied address",
                  description: shortenAddress(walletAddressStr),
                });
              }}
            />
            <IconButton
              icon={<FiExternalLink />}
              href={`https://solscan.io/account/${walletAddressStr}`}
              isExternal
            />
            <IconButton icon={<FiPower />} onClick={handleDisconnectAndClose} />
          </div>
          <div className="pt-8 pb-6 md:pb-6">
            <span className="flex md:mb-1 mb-0">
              <span className="md:numbers-s numbers-sm relative">
                {format2Dec(solBalance)}
                <span className="text-tertiary-text ticker absolute top-[6px] right-[-42px]">
                  SOL
                </span>
              </span>
            </span>
            <span className="font-medium text-tertiary-text">
              ~${format2Dec(solPrice * solBalance)}
            </span>
          </div>
        </>
      )}

      <div className="flex flex-col gap-12-px">
        <span className="title-h4">{t("app.priorityFee.title")}</span>
        <div className="flex flex-col gap-8-px">
          <div className="flex flex-row bg-secondary-bg rounded-[10px] p-2-px w-full">
            {tabsContent.map((tab) => (
              <button
                type="button"
                key={tab.type}
                className={cn("rounded-lg py-8-px body-xs-semibold basis-1/4", {
                  "bg-white": tab.type === priorityFeeType,
                })}
                onClick={() => handleTabChange(tab.type)}
              >
                {tab.title}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={`${customPriorityFee} SOL`}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className="w-full text-center outline-none md:body-s-semibold body-xs-semibold bg-secondary-bg py-8-px px-16-px rounded-[10px]"
          />
        </div>
      </div>
    </div>
  );
};
